import {
  CalendarEventCard,
  CalendarMultiEventCard,
} from "@dropbox/dash-component-library/";
import { Text } from "@dropbox/dig-components/typography";
import { PAP_Shown_CalendarAgentMultipleEvents } from "@mirage/analytics/events/types/shown_calendar_agent_multiple_events";
import { PAP_Shown_CalendarAgentSingleEvent } from "@mirage/analytics/events/types/shown_calendar_agent_single_event";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import {
  getCachedOrListConnections,
  getCachedOrListConnectors,
} from "@mirage/service-connectors";
import { tagged } from "@mirage/service-logging";
import { AvailableConnector } from "@mirage/settings/components/connectors";
import { ConnectedConnector } from "@mirage/settings/components/connectors/ConnectedConnector";
import { useDisconnectConnection } from "@mirage/settings/hooks/connections/useDisconnectConnection";
import { useReauthenticateConnection } from "@mirage/settings/hooks/connections/useReauthenticateConnection";
import { useConnectConnector } from "@mirage/settings/hooks/connectors/useConnectConnector";
import {
  useConnections,
  useSetConnections,
} from "@mirage/shared/atoms/connections";
import { useConnectors } from "@mirage/shared/atoms/connectors";
import { type UIConnector } from "@mirage/shared/types";
import i18n from "@mirage/translations";
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarErrorMessage } from "./CalendarErrorMessage";
import { type CalendarChatJson } from "./types";
import { useTranslatedLabels } from "./useTranslatedCalendarLabels";
import {
  convertValidCalendarCardJsonToCalendarEventData,
  getErrorMessage,
  isGoogleCalendarConnectionError,
  isValidCalendarCardJson,
  splitEventsByStatus,
} from "./utils";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";

const logger = tagged("CalendarResponse");

/**
 * Component that adapts calendar data from chat messages and renders calendar cards.
 * Responsible for converting, validating and rendering calendar UI for single and
 * multiple event entries
 */
export const CalendarResponse = ({
  calendarChat,
  conversationId,
  logComposeEvent,
  spanId,
}: {
  calendarChat: CalendarChatJson[] | null;
  conversationId: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  spanId?: string;
}) => {
  const hasLoggedResponse = useRef(false);
  const account = useDropboxAccount();
  const { data: connectors } = useConnectors();
  const { data: connections, loaded: connectionsLoaded } = useConnections();
  const setConnections = useSetConnections();
  const connectConnector = useConnectConnector({
    eventProps: { featureLine: "assist" },
  });
  const disconnectConnection = useDisconnectConnection();
  const reauthenticateConnection = useReauthenticateConnection();

  const [googleCalendarConnector, setGoogleCalendarConnector] =
    useState<UIConnector | null>(null);
  const [loadingGoogleCalendar, setLoadingGoogleCalendar] = useState(false);

  const isGoogleCalendarConnected = connections.some(
    (connection) =>
      connection.id_attributes?.connector?.type === "googleCalendar",
  );

  const {
    getNumAttendeesLabel,
    getFormattedEventTime,
    getDeclinedEventLabel,
    getRelativeTime,
    timeLabel,
    attendingLabel,
    descriptionLabel,
    dateFormatterFn,
    getFullDate,
    showMore,
    showLess,
    guestLabel,
    hostLabel,
    relativeTimeLabel,
  } = useTranslatedLabels();

  const validCalendarEvents = useMemo(() => {
    return (calendarChat || [])
      .filter(isValidCalendarCardJson)
      .map((cardData) =>
        convertValidCalendarCardJsonToCalendarEventData(
          cardData,
          account?.email,
        ),
      );
  }, [calendarChat, account?.email]);

  // Load connections when we have a Google Calendar error
  useEffect(() => {
    if (
      calendarChat &&
      isGoogleCalendarConnectionError(calendarChat) &&
      !connectionsLoaded
    ) {
      getCachedOrListConnections()
        .then((connections) => {
          setConnections({
            data: connections.map((c) => ({
              ...c,
              loading: false,
              syncing: false,
            })),
            loaded: true,
            loading: false,
          });
        })
        .catch((error) => {
          logger.error("Failed to load connections", error);
          setConnections((atom) => ({ ...atom, loading: false }));
        });
    }
  }, [calendarChat, connectionsLoaded, setConnections]);

  // Load Google Calendar connector when needed
  useEffect(() => {
    if (calendarChat && isGoogleCalendarConnectionError(calendarChat)) {
      const existingGoogleCalendar = connectors.find(
        (c) => c.id_attrs?.type === "googleCalendar",
      );

      if (existingGoogleCalendar) {
        setGoogleCalendarConnector(existingGoogleCalendar);
      } else if (!loadingGoogleCalendar && !googleCalendarConnector) {
        setLoadingGoogleCalendar(true);
        getCachedOrListConnectors()
          .then((allConnectors) => {
            const gcalConnector = allConnectors.find(
              (c) => c.id_attrs?.type === "googleCalendar",
            );

            if (gcalConnector) {
              setGoogleCalendarConnector({
                ...gcalConnector,
                loading: false,
                verifiedConnection: false,
              });
            }
          })
          .catch((error) => {
            logger.error("Failed to load Google Calendar connector", error);
          })
          .finally(() => {
            setLoadingGoogleCalendar(false);
          });
      }
    }
  }, [
    calendarChat,
    connectors,
    connections,
    loadingGoogleCalendar,
    googleCalendarConnector,
  ]);

  if (
    !calendarChat ||
    !Array.isArray(calendarChat) ||
    calendarChat.length === 0
  ) {
    logger.warn("Missing or invalid calendar card data");
    return null;
  }

  const errorMessage = getErrorMessage(calendarChat);
  if (errorMessage !== null) {
    logger.warn("Calendar card data contains error message", { errorMessage });

    // Show connector component for Google Calendar connection issues
    if (isGoogleCalendarConnectionError(calendarChat)) {
      if (!connectionsLoaded) {
        return <Text>Loading...</Text>;
      }

      if (loadingGoogleCalendar) {
        return <Text>Loading Google Calendar connector...</Text>;
      }

      if (googleCalendarConnector) {
        const googleCalendarConnection = connections.find(
          (connection) =>
            connection.id_attributes?.connector?.type === "googleCalendar",
        );

        return (
          <>
            <Text>
              {isGoogleCalendarConnected
                ? i18n.t("calendar_connection_error_connected")
                : i18n.t("calendar_connection_error_not_connected")}
            </Text>
            {isGoogleCalendarConnected && googleCalendarConnection ? (
              <ConnectedConnector
                item={googleCalendarConnection}
                connector={googleCalendarConnector}
                onDisconnect={async (connection) => {
                  await disconnectConnection(connection);
                }}
                onReauthenticate={async (connection, connector) => {
                  await reauthenticateConnection(connection, connector);
                }}
              />
            ) : (
              <AvailableConnector
                item={googleCalendarConnector}
                onConnect={() => connectConnector(googleCalendarConnector)}
              />
            )}
          </>
        );
      }

      // Fallback if connector couldn't be loaded
      return (
        <Text>
          Unable to load Google Calendar connector. Please try again later.
        </Text>
      );
    }

    return <Text>{errorMessage}</Text>;
  }

  const hasNoValidCalendarEvents = validCalendarEvents.length === 0;
  if (hasNoValidCalendarEvents) {
    if (calendarChat) {
      logger.warn("No valid calendar events");
      return (
        <CalendarErrorMessage
          conversationId={conversationId}
          logComposeEvent={logComposeEvent}
          spanId={spanId}
        />
      );
    }
    return null;
  }

  // Log PAP event once for any valid calendar response
  if (!hasLoggedResponse.current) {
    hasLoggedResponse.current = true;
    if (validCalendarEvents.length === 1) {
      logComposeEvent(
        PAP_Shown_CalendarAgentSingleEvent({
          dashSurface: "web",
          dashActionSurface: "conversation_page",
          featureLine: "assist",
          actionSurfaceComponent: "compose_chat_pane",
        }),
      );
    } else {
      logComposeEvent(
        PAP_Shown_CalendarAgentMultipleEvents({
          dashSurface: "web",
          dashActionSurface: "conversation_page",
          featureLine: "assist",
          actionSurfaceComponent: "compose_chat_pane",
        }),
      );
    }
  }

  if (validCalendarEvents.length === 1) {
    return (
      <CalendarEventCard
        event={validCalendarEvents[0]}
        labels={{
          getNumAttendeesLabel,
          getFormattedEventTime,
          getRelativeTime,
          timeLabel,
          attendingLabel,
          descriptionLabel,
          dateFormatterFn,
          getFullDate,
          showMore,
          showLess,
          guestLabel,
          hostLabel,
          relativeTimeLabel,
        }}
      />
    );
  }

  const [_attendingEvents, _declinedEvents] =
    splitEventsByStatus(validCalendarEvents);
  const declinedEvents = _declinedEvents.sort(
    (a, b) => a.startTime - b.startTime,
  );
  const attendingEvents = _attendingEvents.sort(
    (a, b) => a.startTime - b.startTime,
  );

  return (
    <CalendarMultiEventCard
      events={attendingEvents}
      declinedEvents={declinedEvents}
      labels={{
        getNumAttendeesLabel,
        getFormattedEventTime,
        dateFormatterFn,
        getFullDate,
        getDeclinedEventLabel,
      }}
    />
  );
};
